import {
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FilterMatchMode } from "primereact/api";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { Column } from "primereact/column";
import { TableHeader } from "../../components/datatable/TableHeader";
import { FormSkeleton } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { getGlobalConfigurationPageLabel } from "../../utils";
import { AppContext } from "../../context/index";
import { Button, Dropdown } from "iuvity-ui-components";
import { Tag } from "iuvity-ui-components/dist/tag";
import { MultiSelect } from 'primereact/multiselect';
import { CSVLink } from "react-csv";
import { registerAudit } from "../../helpers/registerAudit";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

const csvErrorHeaders = [
  "status",
  "error",
  "id",
  "widgets",
  "type",
  "key",
  "es",
  "en",
  "version",
  "createdat",
  "updatedat",
];

export const ErrorLabelsPage = () => {
  const filePath = sessionStorage.getItem('ERRORS::FILEPATH');
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { language, permissiondInfoByRole, errorLabelsList, labelsImportProcessed, selectedApp },
  } = useContext(AppContext);

  const [filteredData, setFilteredData] = useState<any[] | null>(null);
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const customPageLabel = getGlobalConfigurationPageLabel(t, location.pathname);
  const [statusDrodownValue, setStatusDrodownValue] = useState<string>('');
  const [valuesOfColumnFilterFields, setValuesOfColumnFilterFields] = useState<{
    widgets: string[];
    type: string[];
    version: string[];
  }>({
    widgets: [],
    type: [],
    version: [],
  });
  const [disableClearFilterButton, setDisableClearFilterButton] = useState<{
    status: boolean;
    type: boolean;
    widgets: boolean;
    version: boolean;
  }>({
    status: false,
    type: false,
    widgets: false,
    version: false,
  });
  const [disableApplyFilterButton, setDisableApplyFilterButton] = useState<{
    status: boolean;
    type: boolean;
    widgets: boolean;
    version: boolean;
  }>({
    status: false,
    type: false,
    widgets: false,
    version: false,
  });

  const columns = useMemo(() => {
    return [
      {
        field: "status",
        header: t("Translations.statusLabel"),
      },
      {
        field: "error",
        header: t("Translations.statusLabel"),
      },
      {
        field: "id",
        header: "ID",
      },
      {
        field: "widgets",
        header: t("Translations.functionalityLabel"),
      },
      {
        field: "type",
        header: t("Translations.typeLabel"),
      },
      {
        field: "key",
        header: t("Translations.keyLabel"),
      },
      {
        field: "esvalue",
        header: t("Translations.esvalueLabel"),
      },
      {
        field: "envalue",
        header: t("Translations.envalueLabel"),
      },
      {
        field: "version",
        header: t("Translations.releaseversionLabel"),
      },
      // {
      //   field: "createdat",
      //   header: t("Translations.createdatLabel"),
      // },
      // {
      //   field: "updatedat",
      //   header: t("Translations.updatedatLabel"),
      // },
    ];
  }, [t]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    type: { value: null, matchMode: FilterMatchMode.IN },
    widgets: { value: null, matchMode: FilterMatchMode.IN },
    version: { value: null, matchMode: FilterMatchMode.IN },
    error: { value: null, matchMode: FilterMatchMode.IN },
  };

  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState(defaultFilterValues);

  const onClearFilters = () => {
    setFilteredData(errorLabelsList);
  };

  const onClearFiltersColumn = () => {
    setFilters(defaultFilterValues);
    setStatusDrodownValue('');
    setValuesOfColumnFilterFields({
      widgets: [],
      type: [],
      version: [],
    });
  };

  const onClearFiltersPerTable = ( options: any, filtersFromSession: any ) => {
    setFilters({
      global: { value: options?.field === 'global' ? null : filtersFromSession?.global?.value, matchMode: FilterMatchMode.CONTAINS },
      status: { value: options?.field === 'status' ? null : filtersFromSession?.status?.value, matchMode: FilterMatchMode.EQUALS },
      type: { value: options?.field === 'type' ? null : filtersFromSession?.type?.value, matchMode: FilterMatchMode.IN },
      widgets: { value: options?.field === 'widgets' ? null : filtersFromSession?.widgets?.value, matchMode: FilterMatchMode.IN },
      version: { value: options?.field === 'version' ? null : filtersFromSession?.version?.value, matchMode: FilterMatchMode.IN },
      error: { value: options?.field === 'error' ? null : filtersFromSession?.error?.value, matchMode: FilterMatchMode.IN },
    });

    // luego de dar click en limpiar, elimina los criterios de búsqueda.
    setDisableClearFilterButton((prev:any)=>({
      ...prev,
      [options?.field]: true,
    }));
    options?.field === 'status'
      ? setStatusDrodownValue('')
      : setValuesOfColumnFilterFields((prev)=>({
          ...prev,
          [options.field]: ['widgets','type','version'].includes(options.field) ? [] : '',
        }));
    setDisableApplyFilterButton((prev)=>({
      ...prev,
      [options?.field]: false
    }));
  };

  const handleModalProps = async (
    type: "EXPORT",
  ) => {
    switch (type) {
      case "EXPORT":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Translations.exportErrorsModalTitle"),
          content: t("Translations.exportErrorsModalContent"),
          onConfirm: () => {
            document.getElementById('export-errors-texts-and-messages')?.click();
            registerAudit({
              role: selectedApp?.rolename,
              group: "",
              tenant: selectedApp?.tenantname,
              environment: selectedApp?.env,
              segment: selectedApp?.segment,
              functionality: "LABELS_FRONT",
              activity: "NOPROCCESS_DOWNLOAD_LABELS_FRONT",
              object: "LABEL",
              initialValue: null,
              finalValue: `${errorLabelsList?.length}`,
              status: "SUCCESS",
              errorCode: "",
              errorDescription: "",
              errorCause: "",
            });
          },
        }));

      default:
        return;
    }
  };

  useEffect(() => {
    if (errorLabelsList) {
      setFilteredData(errorLabelsList);
    } else {
      setFilteredData(null);
    }
  }, [errorLabelsList]);

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`Translations.${col.field === 'error'
              ? 'validations'
              : col.field === 'widgets'
                ? 'functionality'
                : col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  };

  const multiselectFilterTemplate = (options: any, arrayWithoutDuplicates: any) => {
    return (<>
      <label htmlFor={options.field} className="block lb mt-2 mb-1">
        {t(`Translations.${options.field}Label`)}
      </label>
      <MultiSelect
        // @ts-ignore
        value={valuesOfColumnFilterFields[options.field]}
        display="chip"
        options={arrayWithoutDuplicates}
        itemTemplate={ItemsTemplate}
        placeholder={t(`Translations.${options.field}PlaceholderLabel`)} 
        className="mb-2"
        emptyFilterMessage={t("Translations.noAvailableMessage")}
        filter
        onChange={(e)=>{
          setValuesOfColumnFilterFields((prev)=>({
            ...prev,
            [options.field]: e.value,
          }));
          options.filterCallback(e.value);
          setDisableApplyFilterButton((prev)=>({
            ...prev,
            [options.field]: e?.value?.length > 0
          }));
        }}
      />
    </>)
  };

  const filterTemplate = (options: any) => {
    const arrayOriginal = options?.field !== 'widgets'
      ? errorLabelsList?.map((label: any) =>
          ['type'].includes(options.field)
            ? ({
              value: t(`Translations.${label?.type.toLowerCase()}OptionLabel`),
              label: t(`Translations.${label?.type.toLowerCase()}OptionLabel`),
            })
            : ({
              label: label[`${options.field}`]?.split(', ')[0],
              value: label[`${options.field}`]?.split(', ')[0]
            }))
        ?.filter((item: { label: string, value: string }) => item.label)
      : [...new Set(errorLabelsList.flatMap((item: any) => item.widgets))]
        .map((item) => ({
          value: `${item}`.trim(),
          label: `${item}`.trim(),
        }));

    arrayOriginal?.sort((a: any, b: any) => a.label.localeCompare(b.label));
    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) =>
        duplicateObject.label === object.label) === currentIndex;
    });

    return (
      options.field === 'status'
      ? statusFilterTemplate(options)
      : multiselectFilterTemplate(options, arrayWithoutDuplicates)
    )
  };

  const statuses:any = [
    `${t("Translations.INVALIDStatusLabel")}`,
  ];

  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  };

  const statusFilterTemplate = (options: any) => {
    return (
            <div>
              <label htmlFor={options.field} className="block lb mt-2 mb-3">
                {t(`Translations.${options.field}Label`)}
              </label>
              <Dropdown
                label=""
                value={statusDrodownValue}
                options={statuses}
                itemTemplate={statusItemTemplate}
                placeholder={t(`Translations.${options.field}FilterPlaceholder`)}
                className="p-column-filter mb-2"
                showClear={false}
                onChange={(e)=>{
                  setStatusDrodownValue(e.value);
                  options.filterCallback(t("Translations.INVALIDStatusLabel") === e.value ? 'INVALID' : 'VALID');
                  setDisableApplyFilterButton((prev)=>({
                    ...prev,
                    status: true
                  }));
                }}
              />
            </div>
    )
  };

  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        itemID={options?.field}
        label={t("Tenants.applyFilterLabel")}
        className="p-button"
        // @ts-ignore
        disabled={!disableApplyFilterButton[options.field]}
        onClick={() => {
          options.filterApplyCallback();
          setDisableClearFilterButton((prev:any)=>({
            ...prev,
            [options?.field]: false,
          }));
        }}
      />
    )
  };

  const filterClearTemplate = (options: any) => {
    const filtersFromSession = JSON.parse(sessionStorage?.getItem('import-filters-table') ?? '{}')?.filters;

    return (
      <Button
        label={t("Tenants.clearFilterLabel")}
        variant="p-button-text"
        disabled={(filtersFromSession?.hasOwnProperty('global')
            ? !filtersFromSession[options?.field]?.value : true)
            // @ts-ignore
            || disableClearFilterButton[options?.field]}
        onClick={() => onClearFiltersPerTable(options, filtersFromSession)}
      />
    )
  }

  const statusBodyTemplate = (rowData: any) => (
    <Tag
      value={t(`Translations.${rowData.status}StatusLabel`)}
      severity={
        (['VALID'].includes(rowData.status) && 'success') ||
        (['INVALID'].includes(rowData.status) && 'danger') ||
        'warning'
      }
    />
  );

  useEffect(() => {
    if (sessionStorage.getItem('import-filters-table')) {
      const dataSession = JSON.parse(sessionStorage.getItem('import-filters-table') || "");
      const global = dataSession.filters.global;
      setGlobalFilterValue(global);
    }
  }, [filters]);

  return (
    <>
      <TransactionalSubheader
        subheaderTitle={
          <div
            style={{
              marginLeft: '-15px',
            }}
          >
            {t("Translations.errorLabelsPageTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={customPageLabel}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => {
              navigate("/");
              onClearFilters();
            },
          }}
          breadcrumbsItems={[
            {
              label: t("Menu.SetupLabel"),
              disabled: true
            },
            {
              label: t("Translations.translationsTitleFront"),
              command: () => navigate('/translations'),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: t("Translations.importBreadcrumb"),
              command: () => navigate('/translations/import'),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: t("Translations.errorImportLabelsBreadcrumb"),
              style: { fontWeight: "bold" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content ml-0 mr-0">
          <span
            dangerouslySetInnerHTML={
              { __html: t("Translations.errorLabelsPageDescription")
                .replace('{{totalLabelsProcessed}}', (labelsImportProcessed ?? 0) - (errorLabelsList?.length ?? 0))
                .replace('{{totalLabelsToProcess}}', labelsImportProcessed ?? 0) }}
          />
        </div>
      </TransactionalSubheader>

      <SubheaderActions
        className="multiactions-c"
        actions={subheaderActionsPrivilegeValidator([
          {
            id: "111",
            icon: "iu iu-icon-plus",
            label: t("Translations.exportLabelsWithErrorsButton"),
            style: {
              display: (!permissiondInfoByRole.READ && !permissiondInfoByRole.CREATE) ? 'none' : undefined,
            },
            onClick: () => handleModalProps("EXPORT"),
          },
        ])}
      />

      <BoxFull className="dt-c my-4">
        {filteredData ? (
          <DataTable
            header={
              <TableHeader
                title={t("Translations.translationsTitle")}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={filters}
                setFilters={setFilters}
                reload={()=>{}}
                onClearFiltersColumn={onClearFiltersColumn}
                globalFilterValue={globalFilterValue}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={t("TableActions.emptyMessage")}
            rows={10}
            size="normal"
            editMode="row"
            dataKey="id"
            sortField="widgets"
            sortOrder={1}
            filters={filters}
            value={filteredData.map((label: any) => ({
              ...label,
              status: 'INVALID',
              widgets: label?.widgets?.join(", "),
              type: t(`Translations.${label?.type?.toLowerCase()}OptionLabel`),
              // createdat: !!label?.createdat ? formatStringToDate2(label?.createdat, true, t) : null,
              // updatedat: !!label?.updatedat ? formatStringToDate2(label?.updatedat, true, t) : null,
              esvalue: label?.es,
              envalue: label?.en,
            }))}
            scrollable
            resizableColumns
            filterDisplay="menu"
            stateStorage="session"
            stateKey="import-filters-table"
          >
            {selectedColumns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable
                align="left"
                style={{
                  whiteSpace: "normal",
                  minWidth: "14rem",
                }}
                filterField={col.field}
                showFilterMatchModes={false}
                filterMenuStyle={{ width: '23rem'}}
                filter={["id","key","esvalue","envalue","createdat","updatedat"].includes(col.field) ? false : true}
                filterElement={filterTemplate}
                filterApply={filterApplyTemplate}
                filterClear={filterClearTemplate}
                body={(rowData) => col.field === 'status' ? statusBodyTemplate(rowData) : rowData[col.field]}
              />
            ))}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
        )}
      </BoxFull>

      <div
        className="w-full"
        style={{
          paddingRight: '128px',
        }}
      >
        <div
          className="flex w-full my-5 p-0 gap-6"
          style={{
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="p-button-link"
            label={t(`Translations.cancelBtnLabel`)}
            onClick={() => navigate('/translations')}
          />
          <Button
              variant="p-button"
              label={t("Translations.exportBtnLabel")}
              onClick={() => handleModalProps("EXPORT")}
            />
          <CSVLink
            className="p-button ntd"
            id="export-errors-texts-and-messages"
            style={{
              display: "none",
            }}
            headers={csvErrorHeaders.map((key) => ({
              label: key,
              key,
            }))}
            data={errorLabelsList}
            enclosingCharacter={`"`}
            filename={`${t("Translations.errorsLabel")}-${filePath ?? (t("Translations.genericFilePathLabel")+'.csv')}`}
          >
            {t("Translations.errorsReportBtnLabel")}
          </CSVLink>
        </div>
      </div>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
